import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOpenOnNew } from "@/hooks/useOpenOnNew";
import { SortingState } from "@tanstack/react-table";
import { Add } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import { PartnerColumn, PartnerData, PartnerStatus } from "./types";
import DataTable from "@/components/controls/DataTable/DataTable";
import {
  GroupRankResolvers,
  statusRank
} from "@/entities/dataTables/groupOrdering";
import {
  ComputedFields,
  EmptyFilters,
  GenericAggregations
} from "@/services/api/types";
import { createDataMapById, flattenDataByKeys } from "@/utils/datamaps";
import {
  usePartners,
  usePartnersInfinite,
  usePartnersDistinctFilterOptions,
  useResetPartnersInfinite
} from "./hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { EmptyView, ViewDataConfiguration, ViewType } from "@/entities/views/types";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import { ColumnData } from "@/entities/dataTables/types";
import {
  getDefaultColumnsStateWithCustomFields,
  getPageNameFromTableId,
  getTempView
} from "@/entities/dataTables/helpers";
import {
  getPartnerTypeOptions,
  partnerColumnKeys,
  partnerStatusColors,
  partnersSortByOptions
} from "./helpers";
import { usePartnerColumns } from "./columns";
import { usePartnerFilterOptions } from "./filters";
import { UserData } from "@/entities/users/types";
import { IconButton } from "@/components/ui/icon-button";
import { isEqual } from "lodash";
import PartnersBottomKPIBar from "./components/PartnersBottomKPIBar";
import CreatePartnerModal from "./components/CreatePartnerModal";
import { useFieldVisibility, useEntityFields } from "@/pages/settings/hooks";
import EntityLogoIconContainer from "@/components/ui/entity-logo-icon-container";
import DataTableToolbar from "@/components/controls/DataTableToolbar";
import { buildGroupSummaries } from "@/components/controls/DataTable/groupSummaries";
import { StatusBadge } from "@/components/ui/status-badge";
import { getEnumTKey } from "@/utils/strings";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";

const TABLE_ID = "partners-table";

interface Props {
  userData: UserData;
}

// Status groups follow the order its enum declares, not whichever column is sorted.
const GROUP_RANK_RESOLVERS: GroupRankResolvers<PartnerData> = {
  [PartnerColumn.STATUS]: statusRank
};

/**
 * The stored column behind each group option the toolbar offers. The server
 * counts every group over the whole filtered set from these, so a group header
 * reads the same on the first page as on the last. Type is left out: a partner
 * holds a list of types rather than one, which is not a value the server can
 * group on — that option still counts only the rows scrolled to.
 */
const SERVER_GROUP_FIELD: Record<string, keyof PartnerData> = {
  [PartnerColumn.STATUS]: "status",
  [PartnerColumn.COUNTRY]: "country",
  [PartnerColumn.STATE_REGION]: "stateRegion"
};

const Partners: React.FC<Props> = ({ userData }) => {
  const { t } = useTranslation();
  const {
    drawerState,
    openDrawer: openQuickView,
    closeDrawer: closeQuickView
  } = useNewQuickViewDrawer();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  useOpenOnNew(setIsCreateModalOpen);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(EmptyFilters);
  const [sorting, onSortingChange] = useState<SortingState>([
    { id: PartnerColumn.NAME, desc: false }
  ]);
  const [columnsState, setColumnsState] = useState<ColumnData[]>([]);
  const [groupBy, setGroupBy] = useState<string[]>([]);

  const [selectedView, setSelectedView] = useState<ViewDataConfiguration>(
    EmptyView(ViewType.PARTNERS_TABLE)
  );

  const { data: entityFieldsResponse } = useEntityFields(EntityType.PARTNER);
  const customFields = entityFieldsResponse?.fields || [];
  const {
    isFieldVisible,
    filterVisibleColumnKeys,
    isLoaded: fieldsLoaded
  } = useFieldVisibility(EntityType.PARTNER);
  const activePartnerColumnKeys = useMemo(
    () => filterVisibleColumnKeys(partnerColumnKeys),
    [filterVisibleColumnKeys]
  );

  const defaultColumnsState = useMemo(
    () =>
      getDefaultColumnsStateWithCustomFields(
        activePartnerColumnKeys,
        customFields,
        getPageNameFromTableId(TABLE_ID),
        t
      ),
    [activePartnerColumnKeys, customFields, t]
  );

  // Opening a view saved without columns empties the layout; re-seed it then
  // too, still only once the custom fields have loaded.
  const isColumnsStateEmpty = columnsState.length === 0;
  useEffect(() => {
    if (!entityFieldsResponse) return;
    setColumnsState((prev) => (prev.length === 0 ? defaultColumnsState : prev));
  }, [defaultColumnsState, isColumnsStateEmpty, entityFieldsResponse]);

  const handleColumnsChange = useCallback((updatedColumns?: ColumnData[]) => {
    if (updatedColumns) {
      setColumnsState(updatedColumns);
    }
  }, []);

  const sortParams = useMemo(() => {
    return {
      field: sorting[0].id ?? PartnerColumn.NAME,
      asc: !sorting[0].desc
    };
  }, [sorting]);

  const groupField = groupBy[0];
  const serverGroupField = groupField
    ? SERVER_GROUP_FIELD[groupField]
    : undefined;

  const {
    entities: partners,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    isError,
    refetch,
    total,
    aggregations,
    isAggregationsLoading
  } = usePartnersInfinite(
    filters,
    searchTerm ? { term: searchTerm } : undefined,
    serverGroupField,
    undefined,
    sortParams.field,
    sortParams.asc,
    undefined,
    true,
    ComputedFields.MINIMAL
  );
  const resetPartnersInfinite = useResetPartnersInfinite();

  /** Grouped as requested — one entry per distinct value of the group field. */
  const groupSummaries = useMemo(() => {
    if (!serverGroupField) return undefined;
    return buildGroupSummaries(
      aggregations as GenericAggregations<string> | undefined,
      [],
      isAggregationsLoading
    );
  }, [serverGroupField, aggregations, isAggregationsLoading]);

  // Every group the response named is on screen from the first page, with the
  // count the server gave it — including one whose partners have not been paged
  // in yet, which the loaded rows alone could not have revealed.
  const partnerGroups = useMemo(() => {
    if (!groupSummaries) return undefined;

    if (groupField === PartnerColumn.STATUS) {
      // Statuses read in the order their enum declares them, and keep the badge
      // the column itself shows.
      return Object.keys(groupSummaries)
        .sort((a, b) => Number(a) - Number(b))
        .map((value) => {
          const status = Number(value) as PartnerStatus;
          return {
            value,
            label: PartnerStatus[status] ? (
              <StatusBadge statusColor={partnerStatusColors[status]}>
                {t(
                  getEnumTKey("partners", "statusValues", PartnerStatus, status)
                )}
              </StatusBadge>
            ) : (
              t("tables.notAvailable")
            )
          };
        });
    }

    return Object.keys(groupSummaries)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value || t("tables.notAvailable") }));
  }, [groupSummaries, groupField, t]);

  const parentPartnerIds: string[] = useMemo(
    () =>
      flattenDataByKeys(partners, (partner: PartnerData) => [
        partner.parentPartner
      ]),
    [partners]
  );
  const parentPartnersData = usePartners(parentPartnerIds);
  const partnersDataMap = useMemo(
    () =>
      parentPartnersData.entities
        ? createDataMapById(parentPartnersData.entities)
        : {},
    [parentPartnersData.entities]
  );

  const { data: countryData } = usePartnersDistinctFilterOptions(
    PartnerColumn.COUNTRY
  );
  const { data: stateRegionData } = usePartnersDistinctFilterOptions(
    PartnerColumn.STATE_REGION
  );

  const typeOptions = useMemo(
    () => getPartnerTypeOptions(entityFieldsResponse?.fields || []),
    [entityFieldsResponse?.fields]
  );

  const partnersFilterOptions = usePartnerFilterOptions({
    countryValues: countryData?.values || [],
    stateRegionValues: stateRegionData?.values || [],
    typeOptions
  });

  useEffect(() => {
    const tempView = getTempView(selectedView);
    if (tempView?.config?.filters) {
      setFilters(tempView.config.filters);
    }
    if (tempView?.config?.searchTerm) {
      setSearchTerm(tempView.config.searchTerm);
    }
    if (tempView?.config?.groupBy) {
      setGroupBy(tempView.config.groupBy);
    }
  }, [selectedView]);

  const resetPage = () => resetPartnersInfinite();

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasNextPage && !isError) {
      fetchNextPage();
    }
  };

  const selectedColumns = useMemo(() => {
    if (columnsState.length > 0) {
      return columnsState;
    }
    const tempView = getTempView(selectedView);
    return tempView?.config?.columns || selectedView?.config?.columns || [];
  }, [columnsState, selectedView]);

  const columns = usePartnerColumns({
    partnersDataMap,
    selectedColumns,
    customFields,
    isFieldVisible,
    fieldsLoaded
  });

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const onRowClick = useCallback(
    (partner: PartnerData) => {
      openQuickView(partner.id, EntityType.PARTNER);
    },
    [openQuickView]
  );

  return (
    <div data-test="partners-page" className="h-full">
      <PartnersBottomKPIBar totalPartners={total} />
      <div
        data-test="partners-content"
        className="flex flex-col max-h-[calc(var(--app-content-h)-var(--table-bottom-space))] h-full"
      >
        <div
          data-test="partners-header"
          className="flex justify-between p-4 pb-2 h-14"
        >
          <div
            data-test="partners-title"
            className="flex items-center gap-[10px]"
          >
            <EntityLogoIconContainer entityType={EntityType.PARTNER} />
            <Text as="div" variant="lgSubtitleH1" className="p-1">
              {t("partners.title")}
            </Text>
          </div>
          <div data-test="partners-actions" className="flex items-center gap-2">
            <IconButton
              data-test="partners-create-button"
              onClick={handleOpenCreateModal}
              size="lg"
            >
              <Add />
            </IconButton>
          </div>
        </div>
        <DataTableToolbar
          viewType={ViewType.PARTNERS_TABLE}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          filterOptions={partnersFilterOptions}
          filters={filters}
          setFilters={setFilters}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          sorting={sorting}
          defaultColumnsState={defaultColumnsState}
          columnsState={columnsState}
          setColumnsState={setColumnsState}
          sortByOptions={partnersSortByOptions}
          defaultSortByValue="name"
          onSortingChange={onSortingChange}
          tableId={TABLE_ID}
          onColumnsChange={handleColumnsChange}
          customFields={customFields}
        />
        <ToolbarActions
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filters={filters}
          setFilters={setFilters}
          filterOptions={partnersFilterOptions}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          sorting={sorting}
          onSortingChange={onSortingChange}
          sortByOptions={partnersSortByOptions}
          defaultSortByValue="name"
          columnsState={columnsState}
          setColumnsState={setColumnsState}
          defaultColumnsState={defaultColumnsState}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          viewType={ViewType.PARTNERS_TABLE}
          onColumnsChange={handleColumnsChange}
          tableId={TABLE_ID}
          customFields={customFields}
          className="p-2 pr-4"
        />
        <DataTable
          groupRankResolvers={GROUP_RANK_RESOLVERS}
          key={TABLE_ID}
          dataTest={TABLE_ID}
          columns={columns}
          initialSortBy={sorting}
          manualSorting
          onSortingChange={onSortingChange}
          data={partners}
          headerClassName="border-none"
          stickyLeftColumn
          resizableColumns
          className="h-full"
          isInfiniteScroll
          isLoadingMore={isFetchingNextPage}
          isDataLoading={isLoading}
          hasMore={hasNextPage}
          onLoadMore={handleLoadMore}
          loadingThreshold={100}
          groupBy={groupBy}
          groupingConfig={{
            typeLabel: t("partners.bottomKPI.total"),
            groups: partnerGroups,
            groupSummaries
          }}
          enableColumnReorder
          tableId={TABLE_ID}
          selectedView={selectedView}
          columnsState={columnsState}
          onColumnsChange={handleColumnsChange}
          filterOptions={partnersFilterOptions}
          filters={filters}
          setFilters={setFilters}
          hasActiveFilters={!isEqual(filters, EmptyFilters)}
          onClearFilters={() => setFilters(EmptyFilters)}
          onClearSearch={() => setSearchTerm("")}
          isError={isError}
          onRetry={refetch}
          onRowClick={(row) => onRowClick(row.original as PartnerData)}
        />
      </div>
      <CreatePartnerModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        resetPage={resetPage}
      />
      <NewQuickView
        isOpen={drawerState.isOpen}
        entityId={drawerState.entityId}
        entityType={EntityType.PARTNER}
        onClose={closeQuickView}
        userData={userData}
      />
    </div>
  );
};

export default Partners;
